/** 
 * @fileoverview Bill parsing utils for all possible input formats
 * Formats include PDFs, Excel sheets and images - extraction methods present for all
 *  
 * Returns normalized bill data shape => { monthly_bill, kwh_usage, rate_per_kwh, tariff_type }
 */

/*
------------------------------------------------------------------------------------------------------------
EXCEL BILLS - XLS/XLSX converted to plain text for AI extraction
------------------------------------------------------------------------------------------------------------
*/

/**
 * extractTextFromExcel:
 * text from XLS/XLSX extracted using SheetJS
 * window.XLSX loaded via CDN in index.html
 * 
 * @param {string} b64 - base64 encoded excel file
 * @returns {Promise<string|null>} - full text extraction from sheets, or null if failed
 * 
 * @example
 * const text = await extractTextFromExcel(b64Excel);
 * // "Sheet: January\nDate,Amount,kWh\n2025-01-01,3500,1400\n\n"
 */
export async function extractTextFromExcel(b64) {
    return new Promise((resolve) => {
        try {
        // use SheetJS loaded via CDN in index.html
        const XLSX = window.XLSX;
        if (!XLSX) { 
            console.warn("SheetJS (XLSX) not loaded.")
            resolve(null);
            return; 
        }

        // decode base64 to binary
        const binary = atob(b64.split(",")[1]);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        // parse workbook
        const wb = XLSX.read(bytes, { type: "array" });

        // convert all sheets to CSV text
        let text = "";
        wb.SheetNames.forEach(name => {
            const ws = wb.Sheets[name];
            const csv = XLSX.utils.sheet_to_csv(ws, { blankrows: false });
            if (csv.trim()) text += `Sheet: ${name}\n${csv}\n\n`;
        });

        resolve(text.trim() || null);

        } catch (e) {
            console.error("Excel parse error:", e);
            resolve(null);
        }
    });
}

/*
------------------------------------------------------------------------------------------------------------
PDF BILLS - text and scanned
------------------------------------------------------------------------------------------------------------
*/

// TEXT

/**
 * extractTextFromPdf:
 * extracts text from PDF via PDF.js lib
 * regex binary extraction fallback in case of PDF.js failure
 * window.pdfjsLib loaded via CDN in index.html 
 * 
 * @param {string} b64 - base64 encoded PDF file
 * @returns {Promise<string|null>} - full text extraction from pages, null if failed
 * 
 * @example
 * const text = await extractTextFromPdf(b64Pdf);
 * // "CITY OF CAPE TOWN\nElectricity Account\nAmount Due: R3,500.00\n..."
 */

export async function extractTextFromPdf(b64) {
    // PDF.js ATTEMPT
    try {
        const pdfjs = window.pdfjsLib;
        console.log("PDF.js available:", !!pdfjs, "workerSrc:", pdfjs?.GlobalWorkerOptions?.workerSrc);

        if (pdfjs) {
            // decode b64 to Uint8Array
            const binary = atob(b64.split(",")[1]);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
            }

            // load pdf doc
            const pdf = await pdfjs.getDocument({data: bytes}).promise;

            // extract text from pages
            let text = "";
            for (let p = 1; p <= pdf.numPages; p++) {
                const page = await pdf.getPage(p);
                const tc = await page.getTextContent();
                text += tc.items.map(i => i.str).join(" ") + "\n";
            }

            if (text.trim().length > 50) {
                console.log("PDF.js extraction successful, chars:", text.length);
                return text.trim();
            }
        }
    } catch (e) {
        console.warn("PDF.js failed, trying regex fallback:", e.message);
    }

    // REGEX FALLBACK - direct extraction of readable strings from PDF binary
    try {
        const binary = atob(b64.split(",")[1]);

        // extract all readable text strings from PDF binary (ASCII printable chars)
        const matches = binary.match(/[\x20-\x7E]{4,}/g) || [];
        const text = matches.join(" ");

        console.log("PDF regex fallback, chars:", text.length, "preview:", text.slice(0,200));
        return text.length > 50 ? text : null;
    }   catch (e) {
        console.error("PDF regex fallback failed:", e);
        return null;
    }
}

// SCANNED - JPEG EXTRACTION

/**
 * extractJpegsFromPdf(b64):
 * extracts embedded JPEG images from PDF
 * used when PDF scanned (NO text layer) and contains images
 * searches for JPEG markers in PDF binary and extracts base64 images
 * 
 * @param {string} b64 - base64-encoded PDF file
 * @returns {string[]} - array of data URI strings (data:image/jpeg;base64,...) - base64-encoded JPEGs - largest first
 * 
 * @example
 * const jpegs = extractJpegsFromPdf(base64ScannedPdf);
 * // ["data:image/jpeg;base64,/9j/4AAQ...", "data:image/jpeg;base64,/9j/4AAQ..."]
 */
export function extractJpegsFromPdf(b64) {
    try {
        const binary = atob(b64.split(",")[1]);
        const jpegs = [];

        // JPEG markers: FF D8 (start) and FF D9 (end)
        const SOI = "\xFF\xD8"; // start of image  
        const EOI = "\xFF\xD9"; // end of image

        let startIdx = 0;
        while (true) {
            // find next JPEG start marker
            startIdx = binary.indexOf(SOI, startIdx);
            if (startIdx === -1) break;

            // find corresponding end marker
            const endIdx = binary.indexOf(EOI, startIdx + 2);
            if (endIdx === -1) break;

            // extract JPEG data (including markers)
            const jpegBinary = binary.slice(startIdx, endIdx + 2);
      
            // Convert to base64
            const jpegB64 = btoa(jpegBinary);
            jpegs.push(`data:image/jpeg;base64,${jpegB64}`);
      
            // Move past this JPEG for next search
            startIdx = endIdx + 2;    
        }

        // Sort by size (largest first) — usually the main bill image
        jpegs.sort((a, b) => b.length - a.length);
    
        console.log(`Extracted ${jpegs.length} JPEG(s) from PDF`);
        return jpegs;
        
    }   catch (e) {
        console.error("JPEG extraction error:", e);
        return [];
    }
}

// TEXT PARSING - fallback for when AI unavailable

/**
 * extractNumbersFromPdfText:
 * extracts billing data from raw PDF text using regex patterns
 * handles various municipal bill formats (Eskom, CoCT, etc.)
 * FALLBACK - for when AI extraction unavailable (not likely to be used)
 * 
 * @param {string} text - raw text extracted from PDF
 * @returns {{monthly_bill: number|null, kwh_usage: number|null, rate_per_kwh: number|null, tariff_type: "residential"|"commercial"}}
 * 
 * @example
 * const pdfText = await extractTextFromPdf(base64);
 * const data = extractNumbersFromPdfText(pdfText);
 * // { monthly_bill: 3500, kwh_usage: 1400, rate_per_kwh: 2.50, tariff_type: "residential" }
 */
export function extractNumbersFromPdfText(text) {
    // find all numbers in the text (handles multiple formats)
    const nums = [];
    const matches = text.match(/\b\d[\d\s,]*\.?\d{0,4}\b/g) || [];
    matches.forEach(m => {
        const n = parseFloat(m.replace(/[\s,]/g,""));
        if (!isNaN(n) && n > 0) nums.push(n);
    });


    // Find billing period days (normalise kWh to 30-day equivalent)
    let billingDays = 30;
    const daysMatch = text.match(/(\d{2,3})\s*days?/i);
    if (daysMatch) { 
        const d = parseInt(daysMatch[1]); 
        if (d >= 20 && d <= 45) billingDays = d; 
    }

    // find kWh consumption — priority order: explicit Consumption line > x100 meter format > generic kWh match
    let kwh = null;
  
    // 1. Best: "Consumption 17625.200 kWh" explicit line
    const consumptionMatch = text.match(/consumption\s+([\d,.\s]+)\s*kwh/i);
    if (consumptionMatch) {
        const parsed = parseFloat(consumptionMatch[1].replace(/[\s,]/g, ""));
        if (parsed >= 100 && parsed <= 500000) {
            kwh = Math.round(parsed / billingDays * 30);
        }
    }
  
    // 2. "176.252(X100)kW" meter format — multiply by 100
    if (!kwh) {
        const x100Match = text.match(/([\d.]+)\s*\(x100\)\s*kw/i);
        if (x100Match) {
            kwh = Math.round(parseFloat(x100Match[1]) * 100 / billingDays * 30);
        }
    }
    
    // 3. Generic "XXXXX kWh" — but only if it's a large enough number for the bill
    if (!kwh) {
        const kwhMatch = text.match(/(\d{3,}[\d.]*)\s*kwh/i) ||
                        text.match(/units used[^\d]*([\d[\d\s,.]*)/i);
        if (kwhMatch) {
            const parsed = parseFloat(kwhMatch[1].replace(/[\s,]/g, ""));
            if (parsed >= 100 && parsed <= 500000) {
                kwh = Math.round(parsed / billingDays * 30);
            }
        }
    }

    // find monthly_bill — prefer electricity-only subtotal, avoid arrears-inflated totals
    let monthly_bill = null;
    
    // try "current amount due" (this month only, no arrears)
    const currentMatch = text.match(/current amount due[^\d]*([\d][\d\s,.]*)/i) ||
                        text.match(/latest account[^\d]*([\d][\d\s,.]*)/i);
    if (currentMatch) {
        const parsed = parseFloat(currentMatch[1].replace(/[\s,]/g, ""));
        if (parsed >= 100) monthly_bill = parsed;
    }
    
    // fallback: any total (may include arrears)
    if (!monthly_bill) {
        const totalMatch = text.match(/(?:total due|amount due|total payable|total liability)[^\d]*([\d][\d\s,.]*)/i);
        if (totalMatch) {
            const parsed = parseFloat(totalMatch[1].replace(/[\s,]/g, ""));
            if (parsed >= 100) monthly_bill = parsed;
        }
    }

    // find rate per kWh — small number near "kWh" like "1.9948" or "2.50"
    let rate_per_kwh = null;
    const rateMatch = text.match(/R\s*([\d.]+)\s*(?:per\s*kwh|\/ kwh|\/kwh)/i) ||
                        text.match(/([\d.]+)\s*\)\s*(?:consumption|tariff)/i);
    if (rateMatch) {
        const r = parseFloat(rateMatch[1]);
        if (r >= 0.5 && r <= 10) rate_per_kwh = r;
    }

    // detect commercial tariff type
    // detect tariff type — agricultural not handled here (rare, AI path covers it)
    const isCommercial = /small power|large power|commercial|business|body corporate/i.test(text);
    const tariff_type = isCommercial ? "commercial" : "residential";

    console.log("PDF direct parse:", { monthly_bill, kwh, rate_per_kwh, tariff_type });

    return { 
        monthly_bill, 
        kwh_usage: kwh,
        rate_per_kwh,
        tariff_type 
    };
}